import type { QueryResult } from '@apollo/client';
import { QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useEffect, useMemo } from 'react';
import { mapTriageRuns } from 'src/graphql/entities/conversation-triage-run/conversation-triage-run.mappings';
import { GET_CONVERSATION_TRIAGE_RUNS_QUERY } from 'src/graphql/entities/conversation-triage-run/conversation-triage-run.operations';
import {
  type TriageRun,
  TriageRunProgress,
  type TriageRunRaw,
} from 'src/graphql/entities/conversation-triage-run/conversation-triage-run.types';
import { type ConditionInput, type QueryParams, Records } from 'src/graphql/types';
import { sortByDate } from 'src/utils/sorting';

export const TRIAGE_RUN_POLL_INTERVAL_MS = 3000;

interface GetConversationTriageRunQuery {
  getConversationTriageRun: Records<TriageRunRaw>;
}

interface GetConversationTriageRunQueryVariables {
  params: QueryParams;
}

type UseConversationTriageRunsResult = Omit<
  QueryResult<GetConversationTriageRunQuery, GetConversationTriageRunQueryVariables>,
  'data'
> & {
  triageRuns: TriageRun[];
};

export function useConversationTriageRuns(
  params: {
    conversationId?: string | null;
    partnerId?: number | null;
    skip?: boolean;
  } = {},
): UseConversationTriageRunsResult {
  const { conversationId, partnerId, skip = false } = params;

  const filters = useMemo<ConditionInput[]>(() => {
    const nextFilters: ConditionInput[] = [];

    if (conversationId) {
      nextFilters.push({ attribute: 'conversationId', operator: '=', value: conversationId });
    }

    if (typeof partnerId === 'number') {
      nextFilters.push({ attribute: 'partnerId', operator: '=', value: String(partnerId) });
    }

    return nextFilters;
  }, [conversationId, partnerId]);

  const options = useMemo<
    QueryHookOptions<GetConversationTriageRunQuery, GetConversationTriageRunQueryVariables>
  >(
    () => ({
      skip: skip || !conversationId,
      // ttl: 0 opts out of the 12h TTL link cache so each poll/refetch actually
      // hits the network
      context: { ttl: 0 },
      variables: {
        params: {
          filters,
          sorting: { attribute: 'createdAt', desc: false },
        },
      },
    }),
    [conversationId, filters, skip],
  );

  const { data, startPolling, stopPolling, ...rest } = useQuery<
    GetConversationTriageRunQuery,
    GetConversationTriageRunQueryVariables
  >(GET_CONVERSATION_TRIAGE_RUNS_QUERY, options);

  const triageRuns = useMemo<TriageRun[]>(
    () =>
      mapTriageRuns(data?.getConversationTriageRun.records).toSorted(
        sortByDate('timestamp', false),
      ),
    [data?.getConversationTriageRun.records],
  );

  // Poll only while a run is still in flight; stop as soon as everything settles
  // so non-triage / completed conversations do no background work (research D3).
  const hasRunningRun = triageRuns.some((run) => run.progress === TriageRunProgress.Running);

  useEffect(() => {
    if (hasRunningRun) {
      startPolling(TRIAGE_RUN_POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [hasRunningRun, startPolling, stopPolling]);

  return {
    triageRuns,
    startPolling,
    stopPolling,
    ...rest,
  };
}
